import { useContext, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { InShellContext, PageActionsContext } from './pageActionsContext';

/**
 * Puts a screen's page-level commands in the shell's title row.
 *
 * Every list screen used to open with an `<h2>` restating the page under the
 * shell's `<h1>`, and its primary command sat beside that second heading —
 * a whole row spent saying the page's name twice. The command now goes up
 * into the title row and the second heading is gone.
 *
 * Outside the shell (a screen rendered on its own in a unit test or a
 * harness) there is no title row, so the actions render in place: the
 * commands stay reachable and testable either way.
 */
export function PageActions({ children }: { children: ReactNode }) {
  const slot = useContext(PageActionsContext);
  const inShell = useContext(InShellContext);

  if (slot !== null) return createPortal(children, slot);
  // Inside the shell but before the slot has mounted (its first frame):
  // render nothing rather than flash the commands in the wrong place.
  if (inShell) return null;
  return <>{children}</>;
}
